<template>
	<Teleport to="body">
		<div class="dialog-backdrop" :class="`dialog-backdrop--${variant}`" @click.self="onBackdrop">
			<slot />
		</div>
	</Teleport>
</template>

<script setup lang="ts">
import { onMounted, onBeforeUnmount } from 'vue'

const props = withDefaults(
	defineProps<{
		variant?: 'sheet' | 'fullscreen' | 'media'
		closeOnBackdrop?: boolean
		closeOnEsc?: boolean
		lockScroll?: boolean
	}>(),
	{
		variant: 'sheet',
		closeOnBackdrop: true,
		closeOnEsc: true,
		lockScroll: true
	}
)

const emit = defineEmits<{ close: [] }>()

function onBackdrop() {
	if (props.closeOnBackdrop) {
		emit('close')
	}
}

function onKeydown(event: KeyboardEvent) {
	if (props.closeOnEsc && event.key === 'Escape') emit('close')
}

function setBodyLock(lock: boolean) {
	if (!props.lockScroll) return
	if (typeof document === 'undefined' || !document.body) return
	document.body.classList.toggle('overflow-hidden', lock)
}

onMounted(() => {
	setBodyLock(true)
	if (props.closeOnEsc) window.addEventListener('keydown', onKeydown)
})

onBeforeUnmount(() => {
	setBodyLock(false)
	window.removeEventListener('keydown', onKeydown)
})
</script>

<style lang="scss">
.dialog-backdrop {
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	z-index: 999;

	&--sheet {
		background: var(--overlay);
		display: flex;
		justify-content: center;
		align-items: flex-end;
		@media (min-width: $screen-mobile) {
			@include flex-center;
		}
	}

	&--fullscreen {
		background: var(--overlay);
		@include flex-column-center;
	}

	&--media {
		background: rgba(0, 0, 0, 0.9);
		@include flex-center;
	}
}
</style>
